import { getItemFromSessionStorage, setItemInSessionStorage } from 'core-framework/window/storage';
import { SessionStorageKey } from 'core-framework/constant/StorageKeys';
import { SuggestionGroup, SuggestionItem } from 'core/type/SuggestionResult.types';
import queryActivitiesGqlApi from 'store/api/activitiesGqlApi';
import { ActivityGqlOperation, OperationKey, SearchRequestType } from 'store/api/api.types';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { getDetailUrl } from 'core/utils/activityUrlGenerator';
import { IconId } from '@drone-js/core';
import { CategoryCms, CategoryIcon, CategoryId } from 'core/type/Category.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { SearchType } from 'gql/autogen/SearchType';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';
import { SortCode } from 'gql/autogen/SortCode';
import { SortOrder } from 'gql/autogen/SortOrder';

export const mapCategoryIcon = (categories?: string[]): IconId => {
    if (!categories || !categories[0]) return 'fill.activity.ferris-wheel';
    return CategoryIcon[categories[0] as CategoryId].id;
};

export const mapCategoryCms = (categories?: string[]): number => {
    if (!categories || !categories[0]) return 0;
    return CategoryCms[categories[0] as CategoryId];
};

export const OrchestrateTopActivitiesInSessionStorage = (config: BootstrapConfig): void => {
    const origin = config.pageConfig.origin;
    const cultureCode = config.language.cultureCode;
    const cityId = config.geo?.topDestinationCityId || 0;
    // Bad practice to use isB, it's for only quick experiment, should be removed after experiment ends
    const isUseDefaultSortExp = isB(ExperimentId.ACT_5036, config.experiments);

    if (cityId <= 0) return;
    const topActivitiesFromSessionStorage = getItemFromSessionStorage(SessionStorageKey.TOP_ACTIVITIES) as SuggestionGroup | null;

    // Fetch top activities if not in session storage or if the origin is different
    if (!topActivitiesFromSessionStorage || topActivitiesFromSessionStorage.origin !== origin) {
        const RESULT_SIZE = 8;
        queryActivitiesGqlApi({
            args: {
                props: {
                    searchType: SearchType.CITY,
                    searchRequestType: isUseDefaultSortExp ? SearchRequestType.DEFAULT : SearchRequestType.POPULAR_ACTIVITIES,
                    pagination: { number: 1, size: RESULT_SIZE },
                    cityId,
                    defaultSort: !isUseDefaultSortExp
                        ? { code: SortCode.ActivitiesHomepageEmptyStateSearchDropdown, order: SortOrder.Descending }
                        : { code: SortCode.Default, order: SortOrder.Default },
                },
                customStateHandling: true,
            },
            operation: ActivityGqlOperation.search,
            key: OperationKey.EmptyStateSuggestion,
            onSuccess: (_operation, _args, response) => {
                const responseContent: ActivityWithContent[] = response?.result?.activities || [];
                if (!responseContent) return;
                const mappedActivities = responseContent.map((item: ActivityWithContent): SuggestionItem => {
                    const activity = item.content?.activity || {};
                    const activityLocation = activity.location || {};
                    const activitiesCity = activityLocation.city || {};
                    return {
                        id: item.masterActivityId ? item.masterActivityId : activitiesCity?.id || 0,
                        activityId: item.masterActivityId || 0,
                        activityName: activity.title || '',
                        cityId: activity.location?.city?.id || 0,
                        cityName: activity.location?.city?.name || '',
                        countryName: activity.location?.country?.name || '',
                        url: getDetailUrl(item.masterActivityId || 0, cultureCode, activity.location?.city?.id),
                        icon: mapCategoryIcon(activity.categories),
                        categoryCms: mapCategoryCms(activity.categories),
                        type: item.masterActivityId ? TextSearchType.Activity : TextSearchType.City,
                        supplierId: item.masterSupplierId,
                    };
                });
                const topActivitiesGroup: SuggestionGroup = {
                    origin,
                    items: mappedActivities,
                };
                setItemInSessionStorage(SessionStorageKey.TOP_ACTIVITIES, JSON.stringify(topActivitiesGroup));
            },
        });
    }
};
